import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"

import { useEditVendorOrderNewMutation, useLazyGetVendorOrderQuery } from "store/api/vendorApi"
import { DEVICE_DEFAULT_QUANTITIES } from "constants/orderQuantities"
import { orderTypeOptions, productTypeOptions } from "constants/SelectFieldOptions"
import { ApiTypes, UITypes } from "types"
import OrderItemType from "types/enums/OrderItemType"
import { ReactComponent as IconInfo } from "assets/svg/icon-info-message.svg"
import { ReactComponent as IconMenuDown } from "assets/svg/icon-menu-down.svg"
import { ReactComponent as IconNewOrder } from "assets/svg/modal-icon-new-order.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectStatic } from "components/form/SelectStatic"

import { OrderItem } from "../NewVendorOrderModal"
import styles from "../NewVendorOrderModal/index.module.scss"
import { OrderItemRow } from "../NewVendorOrderModal/OrderItemRow"

interface Props {
  orderId: number
  onCancel: () => void
  onSuccess: () => void
}

export const EditVendorOrderModal = ({ orderId, onCancel, onSuccess }: Props) => {
  const [triggerGetVendorOrder, { data, isFetching: isFetchingOrder }] = useLazyGetVendorOrderQuery()
  const [triggerEditVendorOrderNew, { isLoading }] = useEditVendorOrderNewMutation()
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const orderData = data?.data

  useEffect(() => {
    if (orderId) {
      triggerGetVendorOrder(orderId)
    }
  }, [orderId, triggerGetVendorOrder])

  useEffect(() => {
    if (orderData) {
      const items: OrderItem[] = orderData.items.map(item => ({
        id: String(item.id),
        productType: item.product_type,
        quantity: item.quantity,
        orderType: item.item_type
          ? [
              {
                value: item.item_type,
                label: orderTypeOptions.find(opt => opt.value === item.item_type)?.label || "",
              } as UITypes.Option,
            ]
          : [],
        warehouse: item.warehouse_id
          ? [
              {
                value: item.warehouse_id,
                label: item.warehouse || String(item.warehouse_id),
              } as UITypes.Option,
            ]
          : [],
        status: item.status,
      }))
      setOrderItems(items)
    }
  }, [orderData])

  const handleProductTypeSelect = (values: UITypes.Option[]) => {
    if (!values.length) return

    const productType = values[0].value as ApiTypes.Model.OrderProductType
    const defaultQuantity = DEVICE_DEFAULT_QUANTITIES[productType]

    const newItem: OrderItem = {
      id: uuidv4(),
      productType,
      quantity: defaultQuantity,
      orderType: [],
      warehouse: [],
      status: "new",
    }
    setOrderItems([...orderItems, newItem])
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (orderItems.length === 0) {
      toast.error("Please add at least one order item")
      return
    }

    const itemsWithoutOrderType = orderItems.filter(item => !item.orderType.length)
    if (itemsWithoutOrderType.length > 0) {
      toast.error("Please select order type for all items")
      return
    }

    const invalidItems = orderItems.filter(item => !item.warehouse.length)
    if (invalidItems.length > 0) {
      toast.error("Please select warehouse for all items")
      return
    }

    try {
      const editData = {
        items: orderItems.map(item => ({
          id: Number(item.id),
          quantity: item.quantity,
          product_type: item.productType,
          item_type: item.orderType[0].value as OrderItemType,
          warehouse_id: Number(item.warehouse[0].value),
        })),
      }

      const res = await triggerEditVendorOrderNew({
        id: orderId,
        data: editData,
      })
      const error = getErrorMessage(res?.error)

      if (res?.error) {
        toast.error(error)
      } else {
        toast.success("Order has been successfully updated!")
        onSuccess()
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to update order")
    }
  }

  if (isFetchingOrder) {
    return null
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="880px"
          onConfirm={onSubmit}
          onCancel={onCancel}
          isFetching={isLoading}
          confirmButtonText="Confirm"
        >
          <ModalHeader title="Edit order" subtitle="Please input the number of Nuve Thermostats you want to order.">
            <IconNewOrder />
          </ModalHeader>
          <div className={styles.modalContent}>
            <div className={styles.orderDetails}>
              <div className={styles.orderDetailsRow}>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Order Number</span>
                  <span className={styles.orderDetailsValue}>{orderData?.id || ""}</span>
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Creation date</span>
                  <span className={styles.orderDetailsValue}>
                    {orderData ? dayjs(orderData.create_date).format("L") : dayjs().format("L")}
                  </span>
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Brand name</span>
                  <span className={styles.orderDetailsValue}>{orderData?.brand_name || ""}</span>
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Company name</span>
                  <span className={styles.orderDetailsValue}>{orderData?.company_name || ""}</span>
                </div>
              </div>
            </div>
            <div className={styles.separator} />
            <SelectStatic
              values={[]}
              options={productTypeOptions}
              className={styles.productTypeSelect}
              onChange={handleProductTypeSelect}
              contentRenderer={() => (
                <div className={styles.addNewButtonWrapper}>
                  <ButtonNew color="blue" className={styles.addNewButton}>
                    Add new <IconMenuDown className={styles.dropdownIndicator} />
                  </ButtonNew>
                </div>
              )}
              isSearchable={false}
              backspaceDelete={false}
            />
            {orderItems.map((item, index) => {
              const isExisting = !isNaN(Number(item.id))
              return (
                <OrderItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  isEdit={true}
                  orderItems={orderItems}
                  setOrderItems={setOrderItems}
                  isExisting={isExisting}
                />
              )
            })}
            <div className={styles.orderNote}>
              <IconInfo />
              Note: We can provide up to five (5) CWAs at no additional cost for every 20 thermostats ordered.
            </div>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
